<template>
  <div class="banner-management">
    <!-- 轮播图列表 -->
    <div class="table-section">
      <el-card class="table-card">
        <div slot="header" class="card-header">
          <h2 class="page-title">
            轮播图管理
          </h2>
          <div class="header-actions">
            <el-button 
              type="primary" 
              icon="el-icon-plus" 
              @click="handleAdd"
              size="small"
            >
              新增轮播图
            </el-button>
            <el-button 
              type="warning" 
              icon="el-icon-check"
              :disabled="selectedRows.length === 0"
              @click="handleBatchEnable"
              size="small"
            >
              批量启用
            </el-button>
            <el-button 
              type="info" 
              icon="el-icon-close"
              :disabled="selectedRows.length === 0"
              @click="handleBatchDisable"
              size="small"
            >
              批量禁用
            </el-button>
            <el-button 
              icon="el-icon-refresh" 
              @click="handleRefresh"
              :loading="loading"
              size="small"
            >
              刷新
            </el-button>
          </div>
        </div>

        <!-- 模块筛选按钮组 -->
        <div class="module-tabs">
          <el-button-group>
            <el-button 
              :type="activeModule === null ? 'primary' : 'default'"
              @click="handleModuleFilter(null)"
            >
              全部
            </el-button>
            <el-button 
              :type="activeModule === 1 ? 'primary' : 'default'"
              @click="handleModuleFilter(1)"
            >
              创新大赛
            </el-button>
            <el-button 
              :type="activeModule === 2 ? 'primary' : 'default'"
              @click="handleModuleFilter(2)"
            >
              大挑
            </el-button>
            <el-button 
              :type="activeModule === 3 ? 'primary' : 'default'"
              @click="handleModuleFilter(3)"
            >
              小挑
            </el-button>
            <el-button 
              :type="activeModule === 4 ? 'primary' : 'default'"
              @click="handleModuleFilter(4)"
            >
              职规大赛
            </el-button>
            <el-button 
              :type="activeModule === 5 ? 'primary' : 'default'"
              @click="handleModuleFilter(5)"
            >
              新文科大赛
            </el-button>
          </el-button-group>
        </div>

        <!-- 搜索和筛选区域 -->
        <div class="filter-section">
          <el-form :model="searchForm" :inline="true" class="search-form">
            <el-form-item label="关键词">
              <el-input
                v-model="searchForm.keyword"
                placeholder="搜索轮播图名称"
                clearable
                style="width: 200px"
                @keyup.enter.native="handleSearch"
              />
            </el-form-item>
            <el-form-item label="状态">
              <el-select v-model="searchForm.state" placeholder="选择状态" clearable style="width: 120px">
                <el-option label="启用" value="1" />
                <el-option label="禁用" value="0" />
              </el-select>
            </el-form-item>
            <el-form-item label="排序方式" class="search-buttons-item">
              <div class="sort-and-buttons">
                <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
                <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
              </div>
            </el-form-item>
          </el-form>
        </div>

        <el-table
          v-loading="loading"
          :data="tableData"
          @selection-change="handleSelectionChange"
          stripe
          border
          style="width: 100%; table-layout: fixed;"
          empty-text="暂无轮播图数据"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column label="轮播图" width="120" align="center">
            <template slot-scope="scope">
              <div class="banner-image">
                <img 
                  :src="scope.row.image" 
                  :alt="scope.row.name"
                  @error="handleImageError"
                  @load="handleImageLoad(scope.row, $event)"
                  @click="previewImage(scope.row.image)"
                />
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip align="center" />
          <el-table-column label="图片尺寸" width="120" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.imageSize">
                {{ scope.row.imageSize }}
              </span>
              <span v-else class="text-muted">加载中...</span>
            </template>
          </el-table-column>
          <el-table-column label="类型" width="120" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.type === 1 ? 'primary' : 'success'" size="small">
                {{ scope.row.type === 1 ? '独立图片' : '内联链接' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="url" label="跳转链接" min-width="250" show-overflow-tooltip align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.url">{{ scope.row.url }}</span>
              <span v-else class="text-muted">无</span>
            </template>
          </el-table-column>
          <el-table-column prop="state" label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.state === 1 ? 'success' : 'danger'" size="small">
                {{ scope.row.state === 1 ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.createTime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" @click="handleEdit(scope.row)">
                修改
              </el-button>
              <el-button size="mini" type="danger" @click="handleDelete(scope.row)">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.pageNum"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
          />
        </div>
      </el-card>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="bannerForm"
        :model="bannerForm"
        :rules="bannerRules"
        label-width="100px"
      >
        <!-- 赛事选择区域 -->
        <div class="module-select-section">
          <div class="module-select-header">
            <i class="el-icon-setting"></i>
            <span class="module-select-title">选择赛事模块</span>
          </div>
          <el-form-item label="选择赛事" prop="mark" class="module-select-item">
            <el-select
              v-model="bannerForm.mark"
              placeholder="请选择赛事模块"
              style="width: 100%"
              size="medium"
            >
              <el-option label="创新大赛" :value="1" />
              <el-option label="大挑" :value="2" />
              <el-option label="小挑" :value="3" />
              <el-option label="职业规划大赛" :value="4" />
              <el-option label="新文科大赛" :value="5" />
            </el-select>
          </el-form-item>
        </div>

        <!-- 分割线 -->
        <div class="form-divider">
          <div class="divider-line"></div>
          <span class="divider-text">轮播图信息</span>
          <div class="divider-line"></div>
        </div>

        <el-form-item label="轮播图名称" prop="name">
          <el-input v-model="bannerForm.name" placeholder="请输入轮播图名称"></el-input>
        </el-form-item>
        
        <el-form-item label="轮播图类型" prop="type">
          <el-radio-group v-model="bannerForm.type" @change="handleTypeChange">
            <el-radio :label="1">独立图片</el-radio>
            <el-radio :label="2">内联链接</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="轮播图图片" prop="image">
          <el-upload
            ref="imageUpload"
            action="#"
            :file-list="imageFileList"
            :before-upload="beforeImageUpload"
            :on-change="onImageChange"
            :on-remove="onImageRemove"
            :accept="acceptedImageTypes"
            :limit="1"
            :auto-upload="false"
            list-type="picture-card"
            class="banner-image-upload"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip" style="margin-top: 10px;">支持 JPG、PNG 格式，建议尺寸 16:9，只能上传一张</div>
          <div v-if="bannerForm.image || selectedImageFile" style="margin-top: 10px;">
            <el-button size="small" @click="handlePreviewImage">预览图片</el-button>
          </div>
        </el-form-item>
        
        <el-form-item 
          label="跳转链接" 
          prop="url"
          v-if="bannerForm.type === 2"
          required
        >
          <el-input 
            v-model="bannerForm.url" 
            placeholder="请输入完整的线上URL地址，如：https://example.com/news/detail/123"
          />
        </el-form-item>
        
        <el-form-item label="状态" prop="state">
          <el-radio-group v-model="bannerForm.state">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          {{ isEdit ? '更新' : '创建' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="图片预览"
      :visible.sync="previewVisible"
      width="auto"
      center
      @close="handlePreviewClose"
    >
      <div class="image-preview">
        <img :src="previewImageUrl" alt="预览图片" style="max-width: 100%; max-height: 500px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { bannerApi } from '@/api/api';
import { API_BASE_URL } from '@/config';

export default {
  name: 'BannerManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      dialogVisible: false,
      previewVisible: false,
      isEdit: false,
      dialogTitle: '新增轮播图',
      previewImageUrl: '',
      previewImageObjectUrl: null, // 用于存储临时创建的Object URL，关闭时释放
      selectedRows: [],
      
      // 模块筛选
      activeModule: null, // 当前激活的模块，null表示全部，1-创新大赛, 2-大挑, 3-小挑, 4-职规赛, 5-新文科
      
      // 搜索表单
      searchForm: {
        mark: null, // 模块标识，null表示全部，1-创新大赛, 2-大挑, 3-小挑, 4-职规赛, 5-新文科
        keyword: '',
        state: ''
      },
      
      // 分页数据
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      
      // 表格数据
      tableData: [],
      
      // 表单数据
      bannerForm: {
        id: null,
        mark: null, // 模块标识，1-创新大赛, 2-大挑, 3-小挑, 4-职规赛, 5-新文科
        name: '',
        type: 1,
        image: '',
        url: '',
        state: 1
      },
      
      // 表单验证规则
      bannerRules: {
        mark: [
          { required: true, message: '请选择所属模块', trigger: 'change' }
        ],
        name: [
          { required: true, message: '请输入轮播图名称', trigger: 'blur' },
          { min: 1, max: 50, message: '名称长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择轮播图类型', trigger: 'change' }
        ],
        image: [
          { validator: this.validateImage, trigger: 'change' }
        ],
        url: [
          { 
            validator: this.validateUrl, 
            trigger: 'blur' 
          }
        ],
        state: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },
      
      // 图片上传相关
      imageFileList: [],
      selectedImageFile: null, // 选中的图片文件对象
      acceptedImageTypes: '.jpg,.jpeg,.png'
    };
  },
  computed: {
  },
  mounted() {
    // 检查管理员权限
    this.checkAdminPermission();
    // 页面加载时刷新管理员状态
    this.loadData();
  },
  
  methods: {
    // 检查管理员权限
    checkAdminPermission() {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        this.$message.error('请先登录管理员账号');
        this.$router.push({ name: 'AdminLogin' });
        return false;
      }
      return true;
    },
    
    // 模块筛选处理（顶部按钮组）
    handleModuleFilter(module) {
      this.activeModule = module;
      // 设置mark值：null表示全部，1-创新大赛, 2-大挑, 3-小挑, 4-职规赛, 5-新文科
      this.searchForm.mark = module;
      this.pagination.pageNum = 1;
      this.loadData();
    },

    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        const data = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        };
        
        // 添加模块筛选参数（mark值：1-创新大赛, 2-大挑, 3-小挑, 4-职规赛, 5-新文科）
        // 如果activeModule为null，则不传mark参数，查询全部模块
        if (this.searchForm.mark !== null && this.searchForm.mark !== undefined) {
          data.mark = this.searchForm.mark;
        }
        
        // 只添加非空的搜索参数
        if (this.searchForm.keyword && this.searchForm.keyword.trim()) {
          data.keyword = this.searchForm.keyword.trim();
        }
        if (this.searchForm.state !== '' && this.searchForm.state !== null && this.searchForm.state !== undefined) {
          data.state = parseInt(this.searchForm.state);
        }
        
        const response = await bannerApi.getBannerList(data);
        
        // 检查响应是否成功
        if (response.data && response.data.code === 200) {
          // 实际返回数据结构为 response.data.data.list 和 response.data.data.total
          const responseData = response.data.data || {};
          // 使用 list 字段，兼容 records 字段
          this.tableData = responseData.list || responseData.records || [];
          this.pagination.total = responseData.total || 0;
          
          // 数据加载后，检查图片尺寸（处理图片已缓存的情况）
          this.$nextTick(() => {
            this.checkImageSizes();
          });
        } else {
          this.$message.error(response.data?.message || response.data?.msg || '获取数据失败');
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        console.error('错误详情:', error.response?.data || error.message);
        this.$message.error(`加载数据失败: ${error.response?.data?.message || error.message || '未知错误'}`);
      } finally {
        this.loading = false;
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.pageNum = 1;
      this.loadData();
    },
    
    // 重置搜索
    handleReset() {
      this.activeModule = null;
      this.searchForm = {
        mark: null,
        keyword: '',
        state: ''
      };
      this.pagination.pageNum = 1;
      this.loadData();
    },

    // 手动刷新
    handleRefresh() {
      this.loadData();
    },
    
    // 新增
    handleAdd() {
      this.isEdit = false;
      this.dialogTitle = '新增轮播图';
      // 如果当前有选中的模块，默认设置为该模块
      const defaultMark = this.activeModule !== null ? this.activeModule : null;
      this.bannerForm = {
        id: null,
        mark: defaultMark,
        name: '',
        type: 1,
        image: '',
        url: '',
        state: 1
      };
      // 清空图片上传列表和选中的文件
      this.imageFileList = [];
      this.selectedImageFile = null;
      this.dialogVisible = true;
    },
    
    // 编辑
    handleEdit(row) {
      this.isEdit = true;
      this.dialogTitle = '编辑轮播图';
      this.bannerForm = {
        id: row.id,
        mark: row.mark, // 保留原有的mark值
        name: row.name,
        type: row.type,
        image: row.image,
        url: row.url || '',
        state: row.state
      };
      // 设置图片上传列表，显示已有图片
      if (row.image) {
        this.imageFileList = [{
          name: row.name || '轮播图',
          url: row.image
        }];
      } else {
        this.imageFileList = [];
      }
      // 编辑时清空选中的新文件（保留原图片）
      this.selectedImageFile = null;
      this.dialogVisible = true;
    },
    
    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除轮播图"${row.name}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await bannerApi.deleteBanner(row.id);
          if (response.data && response.data.code === 200) {
            this.$message.success(response.data.message || response.data.msg || '删除成功');
            this.loadData();
          } else {
            this.$message.error(response.data?.message || response.data?.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      });
    },
    
    // 批量启用
    handleBatchEnable() {
      this.batchUpdateState(1);
    },
    
    // 批量禁用
    handleBatchDisable() {
      this.batchUpdateState(0);
    },
    
    // 批量更新状态
    async batchUpdateState(state) {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择要操作的数据');
        return;
      }
      
      const action = state === 1 ? '启用' : '禁用';
      this.$confirm(`确定要${action}选中的 ${this.selectedRows.length} 个轮播图吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const ids = this.selectedRows.map(row => row.id);
          // 根据文档，批量更新状态接口支持可选的mark参数
          // 如果当前有选中的模块，则只更新该模块的轮播图
          const requestData = { ids, state };
          if (this.searchForm.mark !== null && this.searchForm.mark !== undefined) {
            requestData.mark = this.searchForm.mark;
          }
          
          const response = await bannerApi.batchUpdateState(requestData);
          
          if (response.data && response.data.code === 200) {
            this.$message.success(response.data.message || response.data.msg || `批量${action}成功`);
            this.loadData();
          } else {
            this.$message.error(response.data?.message || response.data?.msg || `批量${action}失败`);
          }
        } catch (error) {
          console.error(`批量${action}失败:`, error);
          this.$message.error(`批量${action}失败`);
        }
      });
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.bannerForm.validate(async (valid) => {
        if (valid) {
          // 创建时必须有图片文件
          if (!this.isEdit && !this.selectedImageFile) {
            this.$message.warning('请上传轮播图图片');
            return;
          }
          
          // 创建时必须有mark字段
          if (!this.isEdit && !this.bannerForm.mark) {
            this.$message.warning('请选择所属模块');
            return;
          }
          
          this.submitLoading = true;
          try {
            // 构建FormData，用于multipart/form-data格式上传
            const formData = new FormData();
            
            // 添加图片文件（创建时必填，更新时可选）
            if (this.selectedImageFile) {
              formData.append('imageFile', this.selectedImageFile);
            }
            
            // 添加其他字段
            if (this.isEdit) {
              // 更新接口：id必填
              formData.append('id', this.bannerForm.id);
              // 其他字段可选
              if (this.bannerForm.name) formData.append('name', this.bannerForm.name);
              if (this.bannerForm.type !== undefined) formData.append('type', this.bannerForm.type);
              // 内联链接类型必须有url，独立图片类型url可选
              if (this.bannerForm.type === 2) {
                // 内联链接：url必填
                formData.append('url', this.bannerForm.url || '');
              } else if (this.bannerForm.url) {
                // 独立图片：url可选
                formData.append('url', this.bannerForm.url);
              }
              if (this.bannerForm.state !== undefined) formData.append('state', this.bannerForm.state);
              if (this.bannerForm.mark) formData.append('mark', this.bannerForm.mark);
            } else {
              // 创建接口：mark、name、type必填
              formData.append('mark', this.bannerForm.mark);
              formData.append('name', this.bannerForm.name);
              formData.append('type', this.bannerForm.type);
              // 内联链接类型必须有url，独立图片类型url可选
              if (this.bannerForm.type === 2) {
                // 内联链接：url必填
                formData.append('url', this.bannerForm.url || '');
              } else if (this.bannerForm.url) {
                // 独立图片：url可选
                formData.append('url', this.bannerForm.url);
              }
              if (this.bannerForm.state !== undefined) formData.append('state', this.bannerForm.state);
            }
            
            let response;
            if (this.isEdit) {
              // 更新轮播图
              response = await bannerApi.updateBanner(formData);
            } else {
              // 创建轮播图
              response = await bannerApi.createBanner(formData);
            }
            
            if (response.data && response.data.code === 200) {
              this.$message.success(response.data.message || response.data.msg || (this.isEdit ? '更新成功' : '创建成功'));
              this.dialogVisible = false;
              this.loadData();
            } else {
              this.$message.error(response.data?.message || response.data?.msg || (this.isEdit ? '更新失败' : '创建失败'));
            }
          } catch (error) {
            console.error('提交失败:', error);
            this.$message.error(this.isEdit ? '更新失败' : '创建失败');
          } finally {
            this.submitLoading = false;
          }
        }
      });
    },
    
    // 对话框关闭
    handleDialogClose() {
      this.$refs.bannerForm.resetFields();
      // 清空图片上传列表和选中的文件
      this.imageFileList = [];
      this.selectedImageFile = null;
    },
    
    // 图片上传前处理（验证）
    beforeImageUpload(file) {
      const isValidType = this.acceptedImageTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      
      if (!isValidType) {
        this.$message.error('图片格式不支持！只支持 JPG、PNG 格式');
        return false;
      }
      
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        this.$message.error('图片大小不能超过 10MB！');
        return false;
      }
      
      return true;
    },
    
    // 图片选择变化（不自动上传，只保存文件对象）
    onImageChange(file, fileList) {
      if (file.status === 'ready') {
        // 保存选中的文件对象，用于提交时一起上传
        this.selectedImageFile = file.raw;
      }
    },
    
    // 图片移除
    onImageRemove() {
      this.selectedImageFile = null;
      // 编辑时移除新选择的图片，保留原图片
      if (this.isEdit) {
        // 编辑模式下，移除新选择的图片不影响原图片
      } else {
        // 新增模式下，移除图片
        this.bannerForm.image = '';
      }
    },
    
    // 表格选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.pageNum = 1;
      this.loadData();
    },
    
    // 当前页变化
    handleCurrentChange(val) {
      this.pagination.pageNum = val;
      this.loadData();
    },
    
    // 图片预览
    previewImage(url) {
      if (!url) {
        this.$message.warning('图片URL为空');
        return;
      }
      this.previewImageUrl = url;
      this.previewVisible = true;
    },
    
    // 处理预览图片（支持已有图片URL和新选择的文件）
    handlePreviewImage() {
      if (this.selectedImageFile) {
        // 预览新选择的文件
        // 如果之前有临时URL，先释放
        if (this.previewImageObjectUrl) {
          URL.revokeObjectURL(this.previewImageObjectUrl);
        }
        const url = URL.createObjectURL(this.selectedImageFile);
        this.previewImageUrl = url;
        this.previewImageObjectUrl = url; // 保存临时URL，关闭时释放
        this.previewVisible = true;
      } else if (this.bannerForm.image) {
        // 预览已有图片
        this.previewImageUrl = this.bannerForm.image;
        this.previewImageObjectUrl = null; // 已有图片URL不需要释放
        this.previewVisible = true;
      }
    },
    
    // 预览对话框关闭时释放临时URL
    handlePreviewClose() {
      if (this.previewImageObjectUrl) {
        URL.revokeObjectURL(this.previewImageObjectUrl);
        this.previewImageObjectUrl = null;
      }
    },
    
    // 自定义图片验证规则
    validateImage(rule, value, callback) {
      // 创建时必须有图片文件
      if (!this.isEdit && !this.selectedImageFile) {
        callback(new Error('请上传轮播图图片'));
        return;
      }
      // 编辑时如果没有新图片，则必须有原图片
      if (this.isEdit && !this.selectedImageFile && !this.bannerForm.image) {
        callback(new Error('请上传轮播图图片或保留原图片'));
        return;
      }
      callback();
    },
    
    // 自定义URL验证规则
    validateUrl(rule, value, callback) {
      // 内联链接类型（type=2）时，URL必填
      if (this.bannerForm.type === 2) {
        if (!value || !value.trim()) {
          callback(new Error('内联链接类型必须填写跳转链接'));
          return;
        }
        // 验证是否为有效的完整URL格式（包含http://或https://）
        const urlPattern = /^https?:\/\/.+/;
        if (!urlPattern.test(value.trim())) {
          callback(new Error('请输入完整的线上URL地址，如：https://example.com/news/detail/123'));
          return;
        }
      }
      // 独立图片类型（type=1）时，URL可选，不验证
      callback();
    },
    
    // 类型变化处理
    handleTypeChange(value) {
      // 切换到独立图片时，清空URL（独立图片不需要跳转链接）
      if (value === 1) {
        this.bannerForm.url = '';
      }
      // 切换类型时不立即校验，只在提交时校验
    },
    
    // 图片加载错误
    handleImageError(event) {
      event.target.src = require('@/assets/zanwu.png');
    },
    
    // 图片加载完成，获取尺寸
    handleImageLoad(row, event) {
      const img = event.target;
      if (img.naturalWidth && img.naturalHeight) {
        // 使用 Vue.set 或直接赋值来更新响应式数据
        this.$set(row, 'imageSize', `${img.naturalWidth} × ${img.naturalHeight}`);
      }
    },
    
    // 检查图片尺寸（处理图片已缓存的情况）
    checkImageSizes() {
      this.$nextTick(() => {
        // 查找表格中所有图片元素
        const images = this.$el.querySelectorAll('.banner-image img');
        images.forEach((img, index) => {
          const row = this.tableData[index];
          if (!row) return;
          
          if (img.complete && img.naturalWidth && img.naturalHeight) {
            // 图片已加载完成，直接获取尺寸
            if (!row.imageSize) {
              this.$set(row, 'imageSize', `${img.naturalWidth} × ${img.naturalHeight}`);
            }
          } else if (!img.complete) {
            // 图片未加载完成，等待load事件（已在模板中绑定handleImageLoad）
            // 如果图片加载失败，设置超时处理
            setTimeout(() => {
              if (!row.imageSize) {
                this.$set(row, 'imageSize', '加载失败');
              }
            }, 5000); // 5秒超时
          }
        });
      });
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.banner-management {
  padding: 0;
}

.table-section {
  margin-bottom: 24px;
}

.table-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.table-card .el-card__body {
  padding: 0 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header .page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 模块筛选按钮组样式 */
.module-tabs {
  margin-bottom: 20px;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.module-tabs .el-button-group {
  display: flex;
  gap: 0;
}

.module-tabs .el-button {
  border-radius: 0;
  margin-right: 0;
}

.module-tabs .el-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.module-tabs .el-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* 搜索筛选区域样式 */
.filter-section {
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 6px;
}

.search-form {
  margin: 0;
}

.search-form .el-form-item {
  margin-bottom: 0;
  margin-right: 12px;
}

.search-buttons-item .el-form-item__content {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.sort-and-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  flex-shrink: 0;
}

.pagination-wrapper {
  padding: 20px 0;
  text-align: right;
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.dialog-footer {
  text-align: right;
}

.image-preview {
  text-align: center;
}

.banner-image {
  width: 80px;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
  margin: 0 auto;
}

.banner-image:hover {
  transform: scale(1.05);
}

.banner-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.text-muted {
  color: #999;
}

/* 表格样式优化 */
.el-table {
  border-radius: 8px;
  overflow: hidden;
}

.el-table th {
  background: #fafbfc;
  color: #222;
  font-weight: 600;
}

.el-table td {
  padding: 12px 0;
}

/* 卡片样式优化 */
.el-card {
  border: 1px solid #f0f0f0;
}

.el-card__header {
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
  padding: 16px 20px;
}

.el-card__body {
  padding: 20px;
}

/* 按钮样式优化 - 使用 Element UI 默认颜色 */

/* 状态开关样式 */
.el-switch {
  transform: scale(0.9);
}

/* 分页样式优化 */
.el-pagination {
  text-align: right;
}

.el-pagination .el-pager li.active {
  background: #409eff;
  color: #fff;
}

.el-pagination .el-pager li:hover {
  color: #409eff;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .module-tabs {
    padding: 15px 0;
  }
  
  .module-tabs .el-button-group {
    flex-wrap: wrap;
    width: 100%;
  }
  
  .module-tabs .el-button {
    flex: 1;
    min-width: calc(50% - 4px);
    margin-bottom: 8px;
  }
  
  .module-tabs .el-button:first-child {
    border-radius: 4px 4px 0 0;
  }
  
  .module-tabs .el-button:last-child {
    border-radius: 0 0 4px 4px;
  }
  
  .search-form {
    display: flex;
    flex-direction: column;
  }
  
  .search-form .el-form-item {
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .filter-section {
    padding: 15px;
  }
  
  .sort-and-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

/* 表格容器样式优化 */
.table-card {
  overflow-x: auto;
}

/* 确保表格占满容器 */
.el-table {
  width: 100% !important;
}

.el-table__body-wrapper {
  width: 100% !important;
}

.el-table__header-wrapper {
  width: 100% !important;
}

/* 图片上传组件样式 */
.banner-image-upload {
  margin-top: 10px;
}

.banner-image-upload .el-upload--picture-card {
  width: 148px;
  height: 148px;
  line-height: 148px;
}

.banner-image-upload .el-upload--picture-card i {
  font-size: 28px;
  color: #8c939d;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

/* 赛事选择区域样式 */
.module-select-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 15px 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.module-select-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.module-select-header i {
  font-size: 18px;
  color: #409eff;
  margin-right: 8px;
}

.module-select-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.module-select-item {
  margin-bottom: 0;
}

.module-select-item .el-form-item__label {
  font-weight: 500;
  color: #606266;
}

/* 分割线样式 */
.form-divider {
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: #909399;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, transparent, #e4e7ed, transparent);
}

.divider-text {
  padding: 0 16px;
  font-size: 14px;
  color: #909399;
  white-space: nowrap;
}
</style>
